import { Button } from '@neo/index'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { useEffect, useRef, useState } from 'react'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { StepsBreadcrumbs } from 'v2/shared/components/steps-breadcrumbs'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { useAddCardOrCashfreePayMutation, useBillingInformationMutation } from '../mutations'
import { useAccountDetailsQuery } from '../queries'
import { BillingInformation } from '../settings/billing-information'
import { useIsCashfreeProvider } from '../use-billing-provider'
import { AddCard } from './add-card'

interface AddFirstCardProps {
  isOpen: boolean
  onClose: () => void
}

export const AddFirstCard = ({ isOpen, onClose }: AddFirstCardProps) => {
  const { mutate: addCard, isPending: isAddingCard } = useAddCardOrCashfreePayMutation()
  const { mutate: updateBillingInformation, isPending: isUpdatingBillingInformation } =
    useBillingInformationMutation()
  const isCashfree = useIsCashfreeProvider()
  const { data: accountDetails } = useAccountDetailsQuery()
  const [currentStep, setCurrentStep] = useState(1)

  // This first-card wizard bypasses AddCardModal, so it needs its own funnel-start
  // call or the funnel loses first-card starts. Fires when the wizard opens (step 1),
  // deferred until account details resolve the real gateway.
  const addCardStartedRef = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      addCardStartedRef.current = false
      return
    }
    if (!accountDetails || addCardStartedRef.current) return
    addCardStartedRef.current = true
    track(AnalyticsEvents.ADD_CARD_STARTED, {
      gateway: isCashfree ? 'cashfree' : 'stripe',
      is_first_card: true,
    })
  }, [isOpen, accountDetails, isCashfree])
  const steps = [
    { number: 1, label: 'Add Billing Address' },
    { number: 2, label: 'Add Card' },
  ]

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <BillingInformation
            variant="modal"
            onNext={() => setCurrentStep(2)}
            updateBillingInformation={updateBillingInformation}
            isUpdatingBillingInformation={isUpdatingBillingInformation}
          />
        )
      case 2:
        return <AddCard onClose={onClose} addCard={addCard} isPending={isAddingCard} />
      default:
        return (
          <BillingInformation
            variant="modal"
            onNext={() => setCurrentStep(2)}
            updateBillingInformation={updateBillingInformation}
            isUpdatingBillingInformation={isUpdatingBillingInformation}
          />
        )
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[calc(100vw-2rem)] sm:max-w-[444px]">
        <DialogHeader>
          <DialogTitle>Add Card</DialogTitle>
        </DialogHeader>
        <StepsBreadcrumbs currentStep={currentStep} steps={steps} onNavigateStep={setCurrentStep} />
        {renderStep()}
        <DialogFooter className="flex flex-row sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={currentStep === 1 ? isUpdatingBillingInformation : isAddingCard}
            data-testid="billing-add-first-card-cancel-btn"
          >
            Cancel
          </Button>
          <FormSubmitButton
            formId={currentStep === 1 ? 'billing-information' : 'add-card'}
            disabled={currentStep === 1 ? isUpdatingBillingInformation : isAddingCard}
            loading={currentStep === 1 ? isUpdatingBillingInformation : isAddingCard}
            data-testid="billing-add-first-card-submit-btn"
          >
            <span>{currentStep === 1 ? 'Next' : 'Add Card'}</span>
          </FormSubmitButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
